import React, { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { Link } from "react-router-dom";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactFormModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedOption, setSelectedOption] = useState<string>("");

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!isOpen) return null;
  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
  };

  const inputClasses: string =
    "border-b-2 border-neutral-500 hover:border-orange-500 text-[#182152] focus:outline-none";

  return (
    <div className="fixed inset-0 h-[100vh] overflow-y-auto m-0 bg-black/70 bg-opacity-10 flex items-center justify-center z-[120] p-5 sm:p-10">
      <div
        className="w-full max-w-md lg:max-w-3xl xl:max-w-4xl rounded-lg animate-in fade-in duration-200"
        ref={modalRef}
      >
        <div className="bg-[#182152] text-white rounded-xl lg:p-7 xl:py-10 p-5 flex flex-col lg:flex-row gap-5 max-h-[90vh] overflow-y-auto">
          {/* Left Section */}
          <div className="flex flex-col justify-between p-5">
            <div className="flex flex-col gap-2">
              <h3 className="text-lg font-bold md:text-2xl lg:text-3xl flex items-center">
                Choose{" "}
                <img src="/logo.png" className="w-32 md:w-44 ml-2" alt="Logo" />
              </h3>
              <h3 className="text-lg font-bold md:text-2xl lg:text-3xl">
                For your Corporate Training Today
              </h3>
            </div>
            <div className="space-y-5 mt-4 text-sm lg:text-base">
              <div className="flex gap-3 items-center p-4">
                <img src="/common/mail.svg" className="w-5" alt="Mail Icon" />
                <a
                  href="mailto:info@dreamweaversgroup.co.in"
                  className="hover:text-orange-400 transition-colors duration-200 flex items-center"
                >
                  info@dreamweaversgroup.co.in
                </a>
              </div>
              <div className="flex gap-3 items-center border-2 border-orange-400 p-4 rounded-lg bg-orange-600/10">
                <img
                  src="/common/telephone.svg"
                  className="w-5"
                  alt="Phone Icon"
                />
                <a
                  href="tel:0181-7102500"
                  className="font-medium hover:text-orange-400 transition-colors duration-200 flex items-center"
                >
                  0181-7102500
                </a>
              </div>
              <div className="flex gap-3 text-sm items-start p-4">
                <img
                  src="/common/location.svg"
                  className="w-5"
                  alt="Address Icon"
                />
                <div className="">
                  SCO 1-12, 4th Floor, PPR Mall, Mithapur Road, Jalandhar,
                  Punjab- 144001
                </div>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <Link
                to="https://www.facebook.com/share/1Biim3dQjF/?mibextid=wwXIfr"
                target="_blank"
              >
                <img src="/common/orange-fb.png" alt="" className="w-8" />
              </Link>
              <Link
                to="https://www.instagram.com/dream_weaversg/"
                target="_blank"
              >
                <img
                  src="/common/contact-instagram.png"
                  alt=""
                  className="w-8"
                />
              </Link>
              <Link
                to="https://youtube.com/@dreamweaversedutracklimited?si=ludHWAbU-KAhbn6B"
                target="_blank"
              >
                <img src="/common/orange-youtube.png" alt="" className="w-8" />
              </Link>
              <Link
                to="https://www.linkedin.com/company/dream-weavers-edutrack-priv-limited/"
                target="_blank"
              >
                <img
                  src="/common/contact-linkedin.png"
                  alt=""
                  className="w-8"
                />
              </Link>
            </div>
          </div>

          {/* Right Section */}
          <div className="p-5 text-sm flex flex-col gap-5 bg-white rounded-lg text-black w-full lg:max-w-lg">
            <h4 className="font-bold">I'm interested in...</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                "Subject Based Training",
                "Compliance Training",
                "Soft Skills",
                "Leadership Development",
                "Other",
              ].map((option) => (
                <button
                  key={option}
                  onClick={() => handleOptionClick(option)}
                  className={`px-3 py-2 border duration-200 ease-out transition-all rounded-lg ${
                    selectedOption === option
                      ? "bg-orange-500 text-white"
                      : "bg-white text-neutral-500"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-5 mt-5">
              <input
                type="text"
                placeholder="Your name"
                className={inputClasses}
              />
              <input
                type="email"
                placeholder="Your email"
                className={inputClasses}
              />
              <textarea
                placeholder="Your message"
                className={inputClasses}
              ></textarea>
            </div>
            <button className="px-5 py-2 flex hover:opacity-95 transition-all duration-200 ease-out items-center bg-orange-500 text-white rounded-lg w-fit">
              <Send className="w-3.5 mr-1" />
              Send Message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFormModal;
